import { Link, Subtitle2, Text, Title3 } from "@fluentui/react-components";
import { ExternalLayout } from "layouts/externalLayout/externalLayout";
import { useStyles } from "./microphoneHelp.styles";

export const MicrophoneHelp = (): JSX.Element => {
  const classes = useStyles();

  return (
    <ExternalLayout style={{ top: "0px" }}>
      <div className={classes.container}>
        <Title3>Detailed Microphone Access Instructions</Title3>
        <Text className={classes.muted}>
          This guide will help you set up and use your microphone.
        </Text>

        <Subtitle2 className={classes.sectionGap}>
          Desktop/Laptop Browsers (Windows &amp; Mac)
        </Subtitle2>

        <div className={classes.card}>
          <Text weight="semibold">First-Time Setup</Text>
          <ol className={classes.list}>
            <li>
              <Text>
                <b>Browser Permission:</b> When visiting a site that requests
                microphone access, you&apos;ll see a permission popup near the
                address bar.
              </Text>
              <ul className={classes.browserList}>
                <li>
                  <Text>
                    <b>Chrome/Edge/Brave:</b> Click &quot;Allow&quot; in the
                    popup that appears in the upper left corner
                  </Text>
                </li>
                <li>
                  <Text>
                    <b>Firefox:</b> Click &quot;Allow&quot; in the popup that
                    appears at the top of the page
                  </Text>
                </li>
                <li>
                  <Text>
                    <b>Safari:</b> Click &quot;Allow&quot; when prompted
                  </Text>
                </li>
              </ul>
            </li>
            <li className={classes.blockGap}>
              <Text weight="semibold">Selecting Your Microphone</Text>
              <ul className={classes.list}>
                <li>
                  <Text>
                    Most browsers will use your system&apos;s default microphone
                    automatically
                  </Text>
                </li>
                <li>
                  <Text>
                    For multiple microphones, click the camera/microphone icon
                    in your address bar to select your preferred device
                  </Text>
                </li>
              </ul>
            </li>
          </ol>
        </div>

        <div className={classes.card}>
          <Text weight="semibold">Managing Permissions</Text>
          <ul className={classes.list}>
            <li>
              <Text>
                <b>Check Current Status:</b> Look for the microphone icon in
                your address bar—if it has a red &quot;X&quot; or slash, your
                microphone is blocked
              </Text>
            </li>
            <li className={classes.blockGap}>
              <Text>
                <b>Change Settings:</b>
              </Text>
              <ul className={classes.browserList}>
                <li>
                  <Text>
                    <b>Chrome/Edge:</b> Click the lock/info icon in the address
                    bar → Site Settings → Microphone
                  </Text>
                </li>
                <li>
                  <Text>
                    <b>Firefox:</b> Click the lock icon → Connection secure →
                    More information → Permissions
                  </Text>
                </li>
                <li>
                  <Text>
                    <b>Safari:</b> Safari menu → Preferences → Websites →
                    Microphone
                  </Text>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <Subtitle2 className={classes.sectionGap}>Mobile Devices</Subtitle2>

        <div className={classes.card}>
          <Text weight="semibold">Android Browsers</Text>
          <ol className={classes.list}>
            <li>
              <Text>
                <b>Initial Request:</b> Tap &quot;Allow&quot; when prompted to
                give the website microphone access
              </Text>
            </li>
            <li className={classes.blockGap}>
              <Text>
                <b>Managing Permissions:</b>
              </Text>
              <ul className={classes.browserList}>
                <li>
                  <Text>
                    <b>Chrome:</b> Tap the three dots menu → Settings → Site
                    Settings → Microphone
                  </Text>
                </li>
                <li>
                  <Text>
                    <b>Firefox:</b> Tap the three dots menu → Settings → Site
                    permissions → Microphone
                  </Text>
                </li>
              </ul>
            </li>
          </ol>
        </div>

        <div className={classes.card}>
          <Text weight="semibold">iOS/iPadOS Browsers</Text>
          <ol className={classes.list}>
            <li>
              <Text>
                <b>Permission Dialog:</b> Tap &quot;Allow&quot; when the browser
                requests microphone access
              </Text>
            </li>
            <li className={classes.blockGap}>
              <Text>
                <b>System Settings:</b> If denied initially, you&apos;ll need to
                enable it in:
              </Text>
              <ul className={classes.list}>
                <li>
                  <Text>
                    Settings → Privacy → Microphone → Toggle on for your browser
                  </Text>
                </li>
                <li>
                  <Text>
                    Settings → [Browser Name] → Microphone → Toggle on
                  </Text>
                </li>
              </ul>
            </li>
          </ol>
        </div>

        <Subtitle2 className={classes.sectionGap}>Troubleshooting</Subtitle2>

        <div className={classes.card}>
          <Text weight="semibold">Common Issues</Text>
          <ol className={classes.list}>
            <li>
              <Text>
                <b>Microphone Not Detected:</b>
              </Text>
              <ul className={classes.list}>
                <li>
                  <Text>Refresh the page</Text>
                </li>
                <li>
                  <Text>Check if your microphone is properly connected</Text>
                </li>
                <li>
                  <Text>
                    Ensure no other applications are using your microphone
                  </Text>
                </li>
              </ul>
            </li>
            <li className={classes.blockGap}>
              <Text>
                <b>Permission Denied:</b>
              </Text>
              <ul className={classes.list}>
                <li>
                  <Text>
                    <b>Desktop:</b> Look for the camera/microphone icon in the
                    address bar and click to enable
                  </Text>
                </li>
                <li>
                  <Text>
                    <b>Mobile:</b> Check your device&apos;s app permissions in
                    system settings
                  </Text>
                </li>
              </ul>
            </li>
            <li className={classes.blockGap}>
              <Text>
                <b>Poor Audio Quality:</b>
              </Text>
              <ul className={classes.list}>
                <li>
                  <Text>Position yourself 6–12 inches from the microphone</Text>
                </li>
                <li>
                  <Text>Reduce background noise</Text>
                </li>
                <li>
                  <Text>Check your internet connection stability</Text>
                </li>
              </ul>
            </li>
          </ol>

          <Text weight="semibold" className={classes.sectionGap}>
            Browser-Specific Tips
          </Text>
          <ul className={classes.list}>
            <li>
              <Text>
                <b>Chrome Incognito:</b> You&apos;ll need to grant permissions
                each session
              </Text>
            </li>
            <li>
              <Text>
                <b>Safari Private Browsing:</b> Some permissions may need to be
                granted each time
              </Text>
            </li>
            <li>
              <Text>
                <b>Firefox Private Mode:</b> You&apos;ll need to allow
                permissions each session
              </Text>
            </li>
          </ul>
        </div>

        <Subtitle2 className={classes.sectionGap}>
          Testing Your Microphone
        </Subtitle2>

        <div className={classes.card}>
          <ul className={classes.list}>
            <li>
              <Text>
                Before important calls or recordings: visit a microphone testing
                site (like{" "}
                <Link
                  href="https://www.mictests.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  mictests.com
                </Link>
                ), allow microphone access, speak normally, and check if the
                audio level indicators respond.
              </Text>
            </li>
          </ul>
          <Text className={classes.blockGap}>
            Remember that browser updates may occasionally reset your microphone
            permissions. If your previously working microphone stops
            functioning, check your browser&apos;s permission settings first.
          </Text>
        </div>
      </div>
    </ExternalLayout>
  );
};
